import { INextButtonProps } from "./AddDocumentInterfaces";
import { useState, useEffect } from "react";
import { Button, Spinner } from "react-bootstrap";
import "./css/NextButton.css";
import { ComponentStruct } from "../../common/Interfaces";
import { dateFormat, guid_mapping, languages } from "../../common/Contants";
import dayjs from "dayjs";
import { postDocument } from "../../api/simDocuments/PostDocumentService";
import SuccessPopUp from "../generic_components/SuccessPopUp";

export default function NextButton({
  addDocumentState,
  selectedType,
  selectedDescription,
  selectedLanguages,
  selectedUrl,
  selectedFile,
  selectedRadio,
  selectedComment,
  selectedReason,
  onErrorMessages,
}: INextButtonProps) {
  const [disabledNext, setDisabledNext] = useState(true);
  const [load, setLoad] = useState(false);
  const [successPopUp, setSuccessPopUp] = useState(false);
  const isTypeSelected = (type: string) => type !== "deselect";
  const isDescriptionValid = (type: ComponentStruct | null) =>
    type?.label !== "deselect" || type === null;
  const isInputValid = (url: string, file: File | null) =>
    (selectedRadio == "url" && url !== "") ||
    (selectedRadio == "file" && file !== null);
  const isReasonValid = (reason: string | null) =>
    reason === null || reason !== "";

  useEffect(() => {
    onErrorMessages([]);
    if (
      isTypeSelected(selectedType.label) &&
      isDescriptionValid(selectedDescription) &&
      selectedLanguages.length > 0 &&
      isInputValid(selectedUrl, selectedFile) &&
      isReasonValid(selectedReason)
    ) {
      setDisabledNext(false);
    } else {
      setDisabledNext(true);
    }
  }, [
    selectedType,
    selectedDescription,
    selectedLanguages,
    selectedUrl,
    selectedFile,
    selectedReason,
    selectedRadio,
  ]);

  const TypeGuidMapping = (
    type: ComponentStruct,
    description: ComponentStruct | null
  ) => {
    description = description?.label == "deselect" ? null : description;
    const result = guid_mapping.find(
      (item) =>
        item.description?.key === description?.key &&
        item.type?.key === type.key
    );
    return result?.guid ?? "00000000-0000-0000-0000-000000000000";
  };

  useEffect(() => {
    const postData = async () => {
      setLoad(true);
      try {
        await postDocument({
          file: selectedRadio == "file" ? selectedFile : null,
          documentTypeGuid: TypeGuidMapping(selectedType, selectedDescription),
          startDate: addDocumentState.startDate
            ? dayjs(addDocumentState.startDate, dateFormat).format(
                "YYYY-MM-DDTHH:mm:ss"
              )
            : null,
          endDate: addDocumentState.endDate
            ? dayjs(addDocumentState.endDate, dateFormat).format(
                "YYYY-MM-DDTHH:mm:ss"
              )
            : null,
          linkType: selectedRadio,
          url: selectedRadio == "url" ? selectedUrl : null,
          fileName: selectedRadio == "file" ? selectedFile?.name || null : null,
          comment: selectedComment,
          languages:
            selectedLanguages.length == languages.length
              ? ["all"]
              : selectedLanguages,
          productCompany: addDocumentState.selectedCompany?.key || null,
          serialNumber: addDocumentState.serialNumber,
          productNumbers: addDocumentState.productNumbers
            ? addDocumentState.productNumbers
            : null,
          models:
            addDocumentState.selectedModels?.map((model) => model.key) || null,
        });
        onErrorMessages([]);
        setSuccessPopUp(true);
      } catch (error: any) {
        console.log(error);
        // Check for 413 status code in response
        if (
          error.code === "ERR_NETWORK" ||
          (error.message && error.message.includes("Request Entity Too Large"))
        ) {
          onErrorMessages([
            "The uploaded file exceeds the maximum allowed size of 90 MB.",
          ]);
        } else if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const errors = error.response.data.errors;
          const messages = errors.map((err: any) => err.message);
          onErrorMessages(messages);
        } else {
          onErrorMessages([error.message]);
        }
      } finally {
        setLoad(false);
      }
    };

    if (load) {
      postData();
    }
  }, [load]);

  return (
    <>
      {load ? (
        <Spinner animation="border" />
      ) : (
        <Button
          className={`btn btn-primary custom-margin`}
          disabled={disabledNext}
          onClick={() => {
            setLoad(true);
            onErrorMessages([]);
          }}
        >
          Next
        </Button>
      )}
      <SuccessPopUp
        showPopup={successPopUp}
        message="Document was successfully uploaded"
      />
    </>
  );
}
